import { useState, useEffect } from 'react';
import ICAL from 'ical.js';
import { apiFetch } from '../api';

function AirbnbYTD({ bookingData, refreshKey }) {
  const [monthlyData, setMonthlyData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchReservations() {
      try {
        const res = await apiFetch('/api/calendar/airbnb');
        const text = await res.text();
        const parsed = ICAL.parse(text);
        const comp = new ICAL.Component(parsed);
        const vevents = comp.getAllSubcomponents('vevent');

        const currentYear = new Date().getFullYear();

        const reservations = vevents
          .map((ve) => {
            const event = new ICAL.Event(ve);
            const summary = event.summary || '';
            return {
              id: event.uid || '',
              summary,
              checkIn: event.startDate.toJSDate(),
              checkOut: event.endDate.toJSDate(),
              nights: Math.round(
                (event.endDate.toJSDate() - event.startDate.toJSDate()) /
                  (1000 * 60 * 60 * 24)
              ),
            };
          })
          .filter((r) => r.summary !== 'Airbnb (Not available)');

        // Build monthly breakdown for current year with prorated amounts
        const months = {};
        for (let m = 0; m < 12; m++) {
          const label = new Date(currentYear, m).toLocaleDateString('en-US', { timeZone: 'America/Chicago', month: 'short' });
          months[m] = { month: m, label, amount: 0, nights: 0 };
        }

        reservations.forEach((r) => {
          const amount = Number(bookingData[r.id]?.amount || 0);
          const perNight = r.nights > 0 ? amount / r.nights : 0;
          const d = new Date(r.checkIn);
          const end = new Date(r.checkOut);

          while (d < end) {
            if (d.getFullYear() === currentYear) {
              months[d.getMonth()].nights += 1;
              months[d.getMonth()].amount += perNight;
            }
            d.setDate(d.getDate() + 1);
          }
        });

        setMonthlyData(Object.values(months));
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    fetchReservations();
  }, [refreshKey, bookingData]);

  if (loading) return <section className="card"><h2>YTD Airbnb Earnings</h2><p className="loading">Loading...</p></section>;

  const currentMonth = new Date().getMonth();
  const ytdTotal = monthlyData.reduce((sum, m) => sum + m.amount, 0);
  const ytdNights = monthlyData.reduce((sum, m) => sum + m.nights, 0);
  const maxAmount = Math.max(...monthlyData.map((m) => m.amount), 1);

  return (
    <section className="card ytd-section">
      <h2>YTD Airbnb Earnings — {new Date().getFullYear()}</h2>
      <div className="ytd-total">
        <span className="ytd-amount">${Math.round(ytdTotal).toLocaleString()}</span>
        <span className="ytd-label">{ytdNights} nights booked</span>
      </div>
      <div className="ytd-chart">
        {monthlyData.map((m) => {
          const height = m.amount > 0 ? Math.max((m.amount / maxAmount) * 100, 4) : 0;
          const isPast = m.month <= currentMonth;
          return (
            <div key={m.month} className="ytd-bar-col">
              <div className="ytd-bar-wrapper">
                {m.amount > 0 && (
                  <span className="ytd-bar-value">${Math.round(m.amount).toLocaleString()}</span>
                )}
                <div
                  className={`ytd-bar ${isPast ? 'past' : 'future'}${m.month === currentMonth ? ' current' : ''}`}
                  style={{ height: `${height}%` }}
                />
              </div>
              <span className={`ytd-bar-label${m.month === currentMonth ? ' current' : ''}`}>
                {m.label}
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default AirbnbYTD;
